import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Layers, Loader2, PauseCircle, Search, Ticket, X } from "lucide-react";
import { StatusMeter, TeamLogo } from "../bondhouse/FixtureStatus";
import {
  buildFixtureMarketGroups,
  countMarketPicks,
  filterOddsMarketGroups,
  fmtOdd,
  formatKickoffShort,
  formatMarketGroupTitle,
  formatOddPickLabel,
  formatOddPickMeta,
  isSameOddPick,
  marketGridClassForGroup,
  marketGroupKey,
  oddPickKey,
} from "../../utils/bettingDeskHelpers";

const OddsMarketDrawer = ({
  open,
  fixture,
  currentPick,
  busy,
  onClose,
  onSelect,
}) => {
  const [pending, setPending] = useState(null);
  const [marketSearch, setMarketSearch] = useState("");
  const [sectionFilter, setSectionFilter] = useState("all");

  const allGroups = useMemo(
    () => (fixture ? buildFixtureMarketGroups(fixture) : []),
    [fixture]
  );

  const searchedGroups = useMemo(
    () => filterOddsMarketGroups(allGroups, marketSearch),
    [allGroups, marketSearch]
  );

  const visibleGroups = useMemo(() => {
    if (sectionFilter === "all") return searchedGroups;
    return searchedGroups.filter((group) => marketGroupKey(group) === sectionFilter);
  }, [searchedGroups, sectionFilter]);

  const totalMarkets = countMarketPicks(allGroups);
  const visibleMarkets = countMarketPicks(visibleGroups);
  const isSuspended = !!fixture?.suspended;
  const pickBlocked = isSuspended && !currentPick;

  const resetState = useCallback(() => {
    setPending(null);
    setMarketSearch("");
    setSectionFilter("all");
  }, []);

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }
    setPending(currentPick || null);
    setMarketSearch("");
    setSectionFilter("all");
  }, [open, currentPick, fixture?._id, resetState]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, busy, onClose]);

  if (!open || !fixture) return null;

  const home = fixture.teams?.home;
  const away = fixture.teams?.away;
  const league = fixture.league?.name || "League";
  const removing = pending && currentPick && isSameOddPick(pending, currentPick);
  const changed =
    pending &&
    currentPick &&
    !isSameOddPick(pending, currentPick);
  const confirmLabel = removing
    ? "Remove from slip"
    : changed
      ? "Update slip"
      : "Add to slip";

  const handleConfirm = async () => {
    if (!pending || busy || pickBlocked) return;
    const ok = await onSelect?.(pending);
    if (ok !== false) onClose?.();
  };

  const sectionChips = searchedGroups.map((group) => ({
    id: marketGroupKey(group),
    label: formatMarketGroupTitle(group),
    count: group.picks.length,
  }));

  return createPortal(
    <div className="pbd-drawer-root" role="presentation" onClick={() => !busy && onClose?.()}>
      <div
        className="pbd-drawer pbd-drawer-markets"
        role="dialog"
        aria-modal="true"
        aria-label="All markets"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="pbd-drawer-head">
          <div className="pbd-drawer-head-main">
            <div className="pbd-drawer-head-top">
              <p className="pbd-drawer-kicker">
                <Layers size={11} aria-hidden />
                All markets
                {totalMarkets > 0 ? (
                  <span className="pbd-drawer-market-count">{totalMarkets}</span>
                ) : null}
              </p>
              <button
                type="button"
                className="pbd-drawer-close"
                onClick={onClose}
                disabled={busy}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="pbd-drawer-meta">
              <span>{league}</span>
              <span className="pbd-drawer-meta-dot" aria-hidden />
              <span>{formatKickoffShort(fixture)}</span>
            </div>

            <div className="pbd-drawer-match">
              <div className="pbd-drawer-team">
                <TeamLogo team={home} size="sm" />
                <span className="truncate">{home?.name || "Home"}</span>
              </div>
              <StatusMeter fixture={fixture} variant="compact" />
              <div className="pbd-drawer-team pbd-drawer-team-away">
                <TeamLogo team={away} size="sm" />
                <span className="truncate">{away?.name || "Away"}</span>
              </div>
            </div>
          </div>
        </header>

        {isSuspended ? (
          <div className="pbd-drawer-suspended">
            <PauseCircle size={14} aria-hidden />
            <span>
              {currentPick
                ? "Fixture suspended — you can change or remove your slip pick."
                : "Fixture suspended — not accepting new picks."}
            </span>
          </div>
        ) : null}

        {allGroups.length > 0 ? (
          <div className="pbd-drawer-tools">
            <div className="pbd-drawer-search">
              <Search size={14} aria-hidden />
              <input
                type="search"
                placeholder="Search markets or outcomes…"
                value={marketSearch}
                onChange={(e) => {
                  setMarketSearch(e.target.value);
                  setSectionFilter("all");
                }}
              />
            </div>

            {sectionChips.length > 1 ? (
              <div className="pbd-drawer-sections" role="group" aria-label="Market sections">
                <button
                  type="button"
                  className={`pbd-drawer-section-chip ${
                    sectionFilter === "all" ? "pbd-drawer-section-chip-active" : ""
                  }`}
                  onClick={() => setSectionFilter("all")}
                >
                  All
                  <span>{searchedGroups.length}</span>
                </button>
                {sectionChips.map((chip) => (
                  <button
                    key={chip.id}
                    type="button"
                    className={`pbd-drawer-section-chip ${
                      sectionFilter === chip.id ? "pbd-drawer-section-chip-active" : ""
                    }`}
                    onClick={() => setSectionFilter(chip.id)}
                  >
                    <span className="truncate">{chip.label}</span>
                    <span>{chip.count}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="pbd-drawer-body">
          {allGroups.length === 0 ? (
            <div className="pbd-drawer-empty">
              <Layers size={28} strokeWidth={1.5} aria-hidden />
              <p>No published markets for this fixture.</p>
            </div>
          ) : visibleGroups.length === 0 ? (
            <div className="pbd-drawer-empty">
              <Search size={28} strokeWidth={1.5} aria-hidden />
              <p>No markets match your search.</p>
            </div>
          ) : (
            visibleGroups.map((group) => (
              <section key={marketGroupKey(group)} className="pbd-market-group">
                <h3 className="pbd-market-group-title">{formatMarketGroupTitle(group)}</h3>
                <div className={`pbd-market-grid ${marketGridClassForGroup(group)}`.trim()}>
                  {group.picks.map((pick) => {
                    const active =
                      isSameOddPick(pending, pick) || isSameOddPick(currentPick, pick);
                    const onSlip = isSameOddPick(currentPick, pick);
                    return (
                      <button
                        key={oddPickKey(pick)}
                        type="button"
                        className={`pbd-market-pick ${active ? "pbd-market-pick-active" : ""} ${
                          onSlip && !isSameOddPick(pending, pick)
                            ? "pbd-market-pick-on-slip"
                            : ""
                        }`}
                        disabled={busy || pickBlocked}
                        onClick={() => setPending(pick)}
                      >
                        <span className="pbd-market-pick-label">{formatOddPickLabel(pick)}</span>
                        <strong className="tabular-nums">{fmtOdd(pick.value)}</strong>
                      </button>
                    );
                  })}
                </div>
              </section>
            ))
          )}
        </div>

        <footer className="pbd-drawer-foot">
          <div className="pbd-drawer-selection">
            {pending ? (
              <>
                <span className="pbd-drawer-selection-kicker">Selected</span>
                <div className="pbd-drawer-selection-copy">
                  <strong>{formatOddPickLabel(pending)}</strong>
                  <span>{formatOddPickMeta(pending)}</span>
                </div>
                <em className="tabular-nums">@ {fmtOdd(pending.value)}</em>
              </>
            ) : currentPick ? (
              <>
                <Ticket size={14} aria-hidden />
                <div className="pbd-drawer-selection-copy">
                  <strong>{formatOddPickLabel(currentPick)}</strong>
                  <span>{formatOddPickMeta(currentPick)}</span>
                </div>
                <em className="tabular-nums">@ {fmtOdd(currentPick.value)}</em>
              </>
            ) : (
              <span className="pbd-drawer-selection-placeholder">
                Pick an outcome to add it to your slip
              </span>
            )}
          </div>

          <div className="pbd-drawer-actions">
            <button
              type="button"
              className="pbd-drawer-cancel"
              disabled={busy}
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className={`pbd-drawer-confirm ${
                pending && !pickBlocked ? "pbd-drawer-confirm-ready" : ""
              } ${removing ? "pbd-drawer-confirm-remove" : ""}`}
              disabled={!pending || busy || pickBlocked}
              onClick={handleConfirm}
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : null}
              {confirmLabel}
            </button>
          </div>

          {visibleMarkets > 0 && visibleMarkets < totalMarkets ? (
            <p className="pbd-drawer-footnote">
              Showing {visibleMarkets} of {totalMarkets} outcomes
            </p>
          ) : null}
        </footer>
      </div>
    </div>,
    document.body
  );
};

export default OddsMarketDrawer;
